import React, { useState, useEffect } from 'react';
import api from '../utils/api';

function SecurityScore() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSecurityScore();
  }, []);

  const fetchSecurityScore = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/security-score');
      setData(response.data);
    } catch (err) {
      setMessage('Failed to load security score.');
    } finally {
      setLoading(false);
    }
  };

  const copyRecommendations = () => {
    if (!data) return;
    const failed = data.checks.filter(c => !c.passed);
    if (failed.length === 0) {
      navigator.clipboard.writeText('No recommendations. Your system is well hardened.');
      return;
    }
    const text = failed.map(c => `- ${c.name}: ${c.description}`).join('\n');
    navigator.clipboard.writeText(text);
  };

  const copyAllNotes = () => {
    if (!data) return;
    const text = data.checks.map(c => `- ${c.name}: ${c.description} (Status: ${c.status})`).join('\n');
    navigator.clipboard.writeText(text);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreStrokeDashoffset = (score, radius) => {
    const circumference = 2 * Math.PI * radius;
    return circumference - (score / 100) * circumference;
  };

  if (loading) {
    return <div className="loading">Loading security score...</div>;
  }

  return (
    <div className="security-score-container" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div className="page-header" style={{ marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: '28px', fontWeight: '700' }}>Security Score</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>System security audit</p>
        </div>
      </div>

      {message && (
        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
          {message}
        </div>
      )}

      {data && (
        <>
          {/* Score Card */}
          <div className="card" style={{ padding: '32px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '32px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: '140px', height: '140px', flexShrink: 0 }}>
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="60" fill="none" stroke="#e5e7eb" strokeWidth="12" />
                <circle
                  cx="70"
                  cy="70"
                  r="60"
                  fill="none"
                  stroke={getScoreColor(data.score)}
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 60}
                  strokeDashoffset={getScoreStrokeDashoffset(data.score, 60)}
                  style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dashoffset 0.6s ease' }}
                />
              </svg>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '36px', fontWeight: '700', color: getScoreColor(data.score) }}>{data.score}</div>
                <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>{data.grade}</div>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: '250px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: '700' }}>
                Grade {data.grade} — {data.gradeLabel}
              </h2>
              <p style={{ margin: '0 0 8px 0', color: '#374151', fontWeight: '500' }}>
                {data.passedChecks} / {data.totalChecks} checks passed
              </p>
              <p style={{ margin: '0 0 20px 0', color: '#6b7280' }}>{data.summary}</p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  className="btn"
                  onClick={copyRecommendations}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: '#fff',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy Recommendations
                </button>
                <button
                  className="btn"
                  onClick={copyAllNotes}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: '#fff',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  Copy All Notes
                </button>
              </div>
            </div>
          </div>

          {/* Checks List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {data.checks.map((check, index) => (
              <div
                key={index}
                className="card"
                style={{
                  padding: '20px 24px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flex: 1 }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: check.passed ? '#d1fae5' : '#fee2e2',
                    color: check.passed ? '#059669' : '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {check.passed ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                      {check.name}{' '}
                      <span style={{ color: '#9ca3af', fontWeight: '500', fontSize: '14px' }}>({check.weight}%)</span>
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '14px' }}>{check.description}</div>
                  </div>
                </div>
                <div style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  background: check.passed ? '#d1fae5' : '#fee2e2',
                  color: check.passed ? '#059669' : '#dc2626',
                  fontSize: '13px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}>
                  {check.status}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default SecurityScore;
